import update from 'immutability-helper'
import { apiPost, apiPut } from 'logic/api'
import { makeSelectCustomer, setCustomer } from 'logic/customers'
import { makeSelectCurOrg } from 'logic/orgs'
import { aquireCheckoutId, setShippingCosts } from 'logic/payment'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

export const SET_VOUCHER = 'tt/shp/SET_VOUCHER'
export const UNSET_VOUCHER = 'tt/shp/UNSET_VOUCHER'
export const SET_VOUCHER_FROM_LS = 'tt/shp/SET_VOUCHER_FROM_LS'
export const SET_VOUCHER_VALUE = 'tt/shp/SET_VOUCHER_VALUE'
export const SET_ERR = 'tt/shp/voucher/SET_ERR'
export const CREATE_VOUCHER = 'tt/shp/CREATE_VOUCHER'
export const SAVE_VOUCHER = 'tt/shp/SAVE_VOUCHER'
export const SET_DESIGNATION = 'tt/shp/SET_DESIGNATION'
export const SET_AGREE_TERMS = 'tt/shp/SET_AGREE_TERMS'
export const SET_DELIVERY_METHOD = 'tt/shp/SET_DELIVERY_METHOD'
export const SET_STATUS = 'tt/shp/SET_STATUS'
export const SET_DONATION = 'tt/shp/SET_DONATION'
export const SET_DONATION_LABEL = 'tt/shp/SET_DONATION_LABEL'

const initialState = {
  value: 10,
  error: null,
  status: null,
  donation: false,
  donationLabel: false,
  agreeTerms: false,
  deliveryMethod: 'pdf',
}

/* -------------------------------------------------------- REDUCER HELPERS */

const setAndStoreVoucher = (state, voucher, skipLS) => {
  if (!skipLS && process.browser) localStorage.setItem('ttv', JSON.stringify(voucher))
  return update(state, { voucher: { $set: voucher } })
}

const unsetVoucherAndClearLS = state => {
  if (process.browser) {
    localStorage.removeItem('ttv')
    localStorage.removeItem('ttcus')
  }
  return update(state, { voucher: { $set: null } })
}

/* -------------------------------------------------------- REDUCER */

export const reducer = (state = initialState, action) => {
  const actions = {
    [SET_STATUS]: ({ status }) => update(state, { status: { $set: status } }),
    [CREATE_VOUCHER]: () => update(state, { status: { $set: 'loading' } }),
    [SET_ERR]: ({ msg }) => update(state, { error: { $set: msg } }),
    [SET_VOUCHER]: ({ voucher, skipLS }) => setAndStoreVoucher(state, voucher, skipLS),
    [UNSET_VOUCHER]: () => unsetVoucherAndClearLS(state),
    [SET_DESIGNATION]: ({ value }) => update(state, { designation: { $set: value } }),
    [SET_AGREE_TERMS]: ({ value }) => update(state, { agreeTerms: { $set: value } }),
    [SET_DONATION]: ({ value }) => update(state, { donation: { $set: value } }),
    [SET_DONATION_LABEL]: ({ value }) => update(state, { donationLabel: { $set: value } }),
    [SET_VOUCHER_VALUE]: ({ value }) => update(state, { value: { $set: value } }),
    [SET_DELIVERY_METHOD]: ({ name }) => update(state, { deliveryMethod: { $set: name } }),
  }
  return actions[action.type] ? actions[action.type](action) : state
}

/* -------------------------------------------------------- ACTIONS */

export const setVoucherFromLS = () => ({
  type: SET_VOUCHER_FROM_LS,
})

export const createVoucher = () => ({
  type: CREATE_VOUCHER,
})

export const setValue = (value, priceName = null) => ({
  type: SET_VOUCHER_VALUE,
  value,
  priceName,
})

export const setDonation = value => ({
  type: SET_DONATION,
  value,
})

export const setDonationLabel = value => ({
  type: SET_DONATION_LABEL,
  value,
})

export const setDesignation = value => ({
  type: SET_DESIGNATION,
  value,
})

export const setAgreeTerms = value => ({
  type: SET_AGREE_TERMS,
  value,
})

export const setErr = msg => ({
  type: SET_ERR,
  msg,
})

export const setVoucher = (voucher, skipLS = false) => ({
  type: SET_VOUCHER,
  voucher,
  skipLS,
})

export const saveVoucher = id => ({
  type: SAVE_VOUCHER,
  id,
})

export const setDeliveryMethod = name => ({
  type: SET_DELIVERY_METHOD,
  name,
})

export const setStatus = status => ({
  type: SET_STATUS,
  status,
})

/* -------------------------------------------------------- SELECTORS */
export const container = state => state.vouchers

export const makeSelectValue = () =>
  createSelector(
    container,
    state => state.value
  )

export const makeSelectDeliveryMethod = () =>
  createSelector(
    container,
    state => state.deliveryMethod
  )

export const makeSelectDonation = () =>
  createSelector(
    container,
    state => state.donation
  )

export const makeSelectDonationLabel = () =>
  createSelector(
    container,
    state => state.donationLabel
  )

export const makeSelectAgreeTerms = () =>
  createSelector(
    container,
    state => state.agreeTerms
  )

export const makeSelectErr = () =>
  createSelector(
    container,
    state => state.error
  )

export const makeSelectStatus = () =>
  createSelector(
    container,
    state => state.agreeTerms ? state.status : 'missing_data'
  )

export const makeSelectLoading = () =>
  createSelector(
    container,
    state => state.loading,
  )

export const makeSelectVoucher = () =>
  createSelector(
    container,
    state => state.voucher
  )

export const unsetVoucher = () => ({
  type: UNSET_VOUCHER,
})

/* -------------------------------------------------------- SAGAS */

function* doSetVoucherFromLS () {
  if (!process.browser) return

  const ttv = window.localStorage.getItem('ttv')
  if (!ttv) return null

  try {
    yield put(setVoucher(JSON.parse(ttv), false))
  } catch (e) {
    console.error('failed to parse local storage voucher object')
    return null
  }
}

function* buildVoucherData () {
  const org = yield select(makeSelectCurOrg())
  const contact = yield select(makeSelectCustomer())
  return {
    contact,
    org_id: org.id,
    donation: yield select(makeSelectDonation()),
    label: yield select(makeSelectDonationLabel()),
    value: parseInt(yield select(makeSelectValue())) * 100,
    delivery_method: yield select(makeSelectDeliveryMethod()),
  }
}

function* ajaxPostVoucher () {
  const data = yield call(buildVoucherData)
  try {
    const response = yield call(apiPost, '/vouchers', data)
    yield put(setVoucher(response.voucher))
    if (response.contact) yield put(setCustomer(response.contact))
    yield put(setStatus('loadingPaymentForm'))
    yield put(aquireCheckoutId(response.voucher.id, response.voucher.value, data.delivery_method))
    yield put(setStatus('done'))
  } catch (e) {
    console.error('error creating voucher', e)
  }
}

function* ajaxPutVoucher({ id }) {
  const data = yield call(buildVoucherData)
  try {
    const response = yield call(apiPut, `/vouchers/${id}`, data)
    yield put(setVoucher(response.voucher))
    if (response.contact) yield put(setCustomer(response.contact))
    yield put(setStatus('loadingPaymentForm'))
    yield put(aquireCheckoutId(response.voucher.id, response.voucher.value))
    yield put(setStatus('done'))
  } catch (e) {
    console.error('error creating voucher', e)
  }
}

function* doUpatePaymentShippingCosts({ name }) {
  const org = yield select(makeSelectCurOrg())
  const shippingCosts = (name === 'post') ? org.portal.voucher_shipping_fees : 0
  yield put(setShippingCosts(shippingCosts))
}

export const sagas = function* () {
  yield takeLatest(SET_VOUCHER_FROM_LS, doSetVoucherFromLS)
  yield takeLatest(SET_DELIVERY_METHOD, doUpatePaymentShippingCosts)
  yield takeLatest(CREATE_VOUCHER, ajaxPostVoucher)
  yield takeLatest(SAVE_VOUCHER, ajaxPutVoucher)
}
